import moment from 'moment'
import 'moment/locale/es'
import seccionPolizasContables from './seccionPolizasContables'
import polizasTrazabilidadPdfV2 from './PdfV2.0/polizasTrazabilidadPdfV2'

moment.locale('es')

/*
  Hoja con todas las pólizas del ciclo del documento al que pertenece un movimiento, en el
  formato compacto de Reposición de Caja Chica (varias pólizas por hoja, sin salto de página).

  Es el hermano de polizasDocumentoPdf: ése reusa polizaPdf y saca una hoja por póliza con el
  formato oficial; éste sacrifica ese formato para que la cadena completa —recepción, factura,
  pago— quepa de un vistazo. Los dos consumen lo mismo: un arreglo de respuestas de
  polizas/datosPolizas, en el orden en que quien llama las quiere impresas.

  El formato de cada póliza NO vive aquí: está en seccionPolizasContables.js, compartido con el
  reporte de caja chica. Aquí sólo va el envoltorio (encabezado de empresa y estilos).
*/
const polizasTrazabilidadPdf = (values) => {
    const polizas = values.polizas || []

    // Diseño moderno (Configuración → PDF). Se exige además el logo de la empresa: PDFHeaderV2
    // referencia `image: 'logo'` siempre y un base64 vacío hace tronar a pdfmake, mientras que
    // el encabezado clásico de aquí abajo sí sabe omitirlo. Con la lista vacía tampoco hay
    // empresa de dónde leer el diseño, así que ese caso también se queda en el clásico.
    const empresaDiseno = polizas.find((p) => p?.empresa)?.empresa
    if (empresaDiseno?.tipoPDF === 'moderno' && empresaDiseno?.logo?.base64Completa) {
        return polizasTrazabilidadPdfV2(values)
    }

    const estilos = {
        header: { fontSize: 9, bold: true },
        subheader: { fontSize: 8, bold: true },
        tableExample: { margin: [0, 5, 0, 5] },
        tableHeader: { bold: true, fontSize: 12, color: 'black', alignment: 'center' },
        textoChico: { fontSize: 8 },
        tituloTabla: {
            fontSize: 8,
            bold: true,
            fillColor: '#cccccc',
            alignment: 'center',
            border: true,
        },
    }

    if (polizas.length === 0) {
        return {
            pageSize: 'LETTER',
            styles: estilos,
            content: [{ text: 'No hay pólizas que mostrar', alignment: 'center', margin: [0, 40, 0, 0] }],
        }
    }

    // datosPolizas devuelve la empresa dentro de CADA póliza (todas la misma) y no manda
    // logoBiwo, por eso este PDF no lleva el pie de marca que sí tiene el de caja chica.
    const empresa = polizas.find((p) => p?.empresa)?.empresa
    const direccion = empresa
        ? [empresa.calle, empresa.numeroCalle, empresa.numeroInterior].filter(Boolean).join(' ')
        : ''
    const coloniaTexto = empresa?.colonia
        ? [
            empresa.colonia.tipoAsentamiento,
            empresa.colonia.nombre,
            empresa.colonia.cp ? 'C.P. ' + empresa.colonia.cp : '',
            empresa.municipio?.nombre,
            empresa.estado?.abreviatura,
            empresa.pais?.abreviatura,
        ].filter(Boolean).join(' ')
        : ''
    const nombreEmpresa = empresa
        ? (empresa.nombreComercial
            ? empresa.nombreComercial
            : (empresa.persona
                ? empresa.nombre
                : [empresa.nombre, empresa.apellidoPaterno, empresa.apellidoMaterno].filter(Boolean).join(' ')))
        : ''
    const logoEmpresa = empresa?.logo?.base64Completa

    const encabezado = {
        style: 'tableExample',
        table: {
            widths: ['18%', '52%', '30%'],
            layout: { defaultBorder: false },
            body: [
                [
                    logoEmpresa
                        ? { border: [false, false, false, false], image: 'logo', fit: [80, 80] }
                        : { border: [false, false, false, false], text: '' },
                    {
                        border: [false, false, false, false],
                        table: {
                            headerRows: 1,
                            widths: ['100%'],
                            body: [
                                [{ text: nombreEmpresa, style: 'tableHeader' }],
                                [{ text: empresa?.rfc ? 'R.F.C: ' + empresa.rfc : '', style: 'textoChico', alignment: 'center' }],
                                [{ text: direccion, style: 'textoChico', alignment: 'center' }],
                                [{ text: coloniaTexto, style: 'textoChico', alignment: 'center' }],
                                [{ text: 'PÓLIZAS DE LA TRAZABILIDAD', style: 'tableHeader' }],
                            ],
                        },
                        layout: 'noBorders',
                    },
                    {
                        border: [false, false, false, false],
                        table: {
                            headerRows: 1,
                            widths: ['100%'],
                            body: [
                                [{ text: 'Pólizas:', style: 'subheader', alignment: 'center' }],
                                [{ text: String(polizas.length), style: 'textoChico', alignment: 'center' }],
                                [{ text: 'Fecha de Impresión:', style: 'subheader', alignment: 'center' }],
                                [{ text: moment().locale('es').format('LL'), style: 'textoChico', alignment: 'center' }],
                            ],
                        },
                        layout: 'noBorders',
                    },
                ],
            ],
        },
    }

    return {
        content: [
            encabezado,
            // Sin título: el encabezado ya dice qué es esta hoja.
            ...seccionPolizasContables(polizas, { titulo: null }),
        ],
        images: {
            logo: logoEmpresa || '',
        },
        styles: estilos,
        pageSize: 'LETTER',
        pageMargins: [20, 20, 20, 40],
    }
}

export default polizasTrazabilidadPdf
